import { computed, nextTick, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { omit } from 'lodash-es'
import { useLocalStorage } from '@vueuse/core'
import { useApi } from '@provise/composables/api'
import { useCookies } from '@vueuse/integrations/useCookies'
import { jwtDecode } from 'jwt-decode'
import config from '@/config'

export const useUserStore = defineStore('user', () => {
  const user = useLocalStorage('user', {})
  const lastActivity = useLocalStorage('lastActivity', Date.now())
  const isAuthorized = computed(() => user.value?.access_token?.length > 0)
  const cookies = useCookies(['AuthorizationToken'])
  const isValid = computed(() => user.value?.fio && user.value?.email_notifications)
  const isLoggedIn = ref(false)

  let logoutInterval = null
  const TIMEOUT = Number(config.VITE_LOGOUT_TIMEOUT ?? 900000)

  // initial value may be set by SSO redirect url
  const atFromCookie = cookies.get('AuthorizationToken')
  if (atFromCookie) {
    user.value = { ...user.value, access_token: atFromCookie }
  }

  watch(
    isAuthorized,
    async (curr, prev) => {
      // login
      if (curr && !prev) {
        const jwt = user.value.access_token
        const jwtd = jwtDecode(jwt)

        user.value = { ...user.value, sso: jwtd.sso }
        cookies.set('AuthorizationToken', jwt, { path: '/', maxAge: TIMEOUT / 1000 })
        await nextTick()
        await fetchCurrent()
        isLoggedIn.value = true
      }
      // lougout
      if (!curr && prev) {
        cookies.remove('AuthorizationToken')
        isLoggedIn.value = false
      }
    },
    { immediate: true }
  )

  async function fetchCurrent() {
    const { refresh: _fetchCurrentUser, model: _currentUser } = useApi({
      url: 'current_user',
      pick(d) {
        return omit(d, ['deleted', 'ms_entra'])
      },
    })
    await _fetchCurrentUser()
    isLoggedIn.value = true
    user.value = { ...user.value, ..._currentUser.value }
  }

  // with children
  function can(roleName) {
    return hasRole(false, user.value?.roles, roleName)
  }

  // without children
  function is(roleName) {
    return hasRole(true, user.value?.roles, roleName)
  }

  function updateLastActivity() {
    lastActivity.value = Date.now()
  }

  function startLogoutChecker(callback) {
    stopLogoutChecker()
    logoutInterval = setInterval(async () => {
      if (Date.now() - lastActivity.value > TIMEOUT) {
        stopLogoutChecker()
        await callback()
      }
    }, 10000)
  }

  function stopLogoutChecker() {
    if (logoutInterval) {
      clearInterval(logoutInterval)
      logoutInterval = null
    }
  }

  function resetTimer(callback) {
    updateLastActivity()
    startLogoutChecker(callback)
    const jwt = user.value?.access_token
    if (jwt) {
      cookies.set('AuthorizationToken', jwt, {
        path: '/',
        maxAge: TIMEOUT / 1000,
      })
    }
  }

  return {
    user,
    isAuthorized,
    isValid,
    lastActivity,
    isLoggedIn,
    fetchCurrent,
    can,
    is,
    resetTimer,
    updateLastActivity,
    startLogoutChecker,
    stopLogoutChecker,
  }
})

export function hasRole(strict = false, roles = [], rname) {
  if (!Array.isArray(roles)) {
    return false
  }

  const _rname = Array.isArray(rname) ? rname : [rname]

  for (let role of roles) {
    if (role?.service !== config.VITE_SERVICE_SELF) {
      continue
    }

    const hasSelf = _rname.includes(role.name)

    if (strict) {
      return hasSelf
    }

    if (hasSelf || hasRole(false, role.children, _rname)) {
      return true
    }
  }

  return false
}
